import { attachCpqPostMessageBridge } from "../src/cpq-postmessage-bridge";
import type { ThreekitEmbedHandle } from "../src/embed-api";
import { mount, type CpqMountOptions } from "./cpq-app";

function parseOriginsList(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

/**
 * Origins allowed to send `THREEKIT_CPQ_MOUNT` and bridge RPCs into this document.
 * Set `VITE_CPQ_IFRAME_PARENT_ORIGINS` to a comma-separated list (e.g. Oracle CPQ hosts).
 * `http(s)://localhost` and `127.0.0.1` (any port) are always allowed as parent origins for local tests.
 * In dev, `?cpqParents=https://host1,https://host2` is merged in for non-loopback hosts.
 */
export function readCpqIframeParentAllowedOrigins(): string[] {
  /*const fromEnv = parseOriginsList(
    import.meta.env.VITE_CPQ_IFRAME_PARENT_ORIGINS
  );*/
  const fromEnv = parseOriginsList(
    "https://localhost:3030,https://pridemobilitytest1.bigmachines.com,https://pridemobilitytest2.bigmachines.com,https://pridemobilitytest3.bigmachines.com,https://pridemobility.bigmachines.com"
  );
  if (import.meta.env.DEV && typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search).get("cpqParents");
    if (q) return [...fromEnv, ...parseOriginsList(q)];
  }
  return fromEnv;
}

export function isBrowserIframeChild(): boolean {
  return typeof window !== "undefined" && window.parent !== window;
}

/** `postMessage` event.origin for http(s)://localhost or 127.0.0.1 (any port). */
function isLoopbackPostMessageOrigin(origin: string): boolean {
  try {
    const u = new URL(origin);
    const h = u.hostname;
    if (h !== "localhost" && h !== "127.0.0.1") return false;
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function originAllowed(origin: string, allowlist: string[]): boolean {
  if (origin === window.location.origin) return true;
  if (isLoopbackPostMessageOrigin(origin)) return true;
  return allowlist.includes(origin);
}

function replyToSource(ev: MessageEvent, payload: Record<string, unknown>): void {
  const src = ev.source;
  if (src && typeof (src as Window).postMessage === "function") {
    (src as Window).postMessage(payload, ev.origin);
  }
}

/**
 * When the standalone app is loaded inside a foreign iframe (e.g. Oracle CPQ), wait for
 * `THREEKIT_CPQ_MOUNT` instead of calling `mount()` from env. Same protocol as
 * `public/examples/cpq-threekit-iframe-host.html`.
 */
export function runCpqPostMessageIframeHostFlow(
  containerSelector: string,
  onMounted?: (handle: ThreekitEmbedHandle) => void,
  defaultMountOptions?: CpqMountOptions
): void {
  const allowed = readCpqIframeParentAllowedOrigins();
  if (allowed.length === 0) {
    console.warn(
      "[Threekit CPQ] Embedded in an iframe but VITE_CPQ_IFRAME_PARENT_ORIGINS is empty — " +
        "only same-origin and loopback (localhost / 127.0.0.1) parents can postMessage. " +
        "In dev, add ?cpqParents= for other hosts."
    );
  }

  let mounted = false;
  let bridge: ReturnType<typeof attachCpqPostMessageBridge> | null = null;

  function detachBridge(): void {
    bridge?.detach();
    bridge = null;
  }

  const onMessage = (ev: MessageEvent): void => {
    if (!originAllowed(ev.origin, allowed)) return;
    if (mounted) return;
    const data = ev.data;
    if (
      !data ||
      typeof data !== "object" ||
      (data as { type?: unknown }).type !== "THREEKIT_CPQ_MOUNT"
    ) {
      return;
    }
    /** Same fields as `mount(container, options)` — e.g. `showConfigurator: false`, `display`, `configuration`. */
    const opts = {
      ...defaultMountOptions,
      ...((data as { options?: CpqMountOptions }).options ?? {}),
    };
    mounted = true;
    void mount(containerSelector, opts)
      .then((handle) => {
        detachBridge();
        (window as unknown as { threekitCpq: typeof handle }).threekitCpq = handle;
        bridge = attachCpqPostMessageBridge(handle, {
          isOriginAllowed: (origin) => originAllowed(origin, allowed),
          onTeardown: () => {
            bridge = null;
            mounted = false;
            try {
              delete (window as unknown as { threekitCpq?: unknown }).threekitCpq;
            } catch {
              (window as unknown as { threekitCpq?: unknown }).threekitCpq = undefined;
            }
          },
        });
        replyToSource(ev, { type: "THREEKIT_CPQ_READY" });
        onMounted?.(handle);
        if (import.meta.env.DEV) {
          console.info(
            "[Threekit CPQ] Iframe host: mounted via postMessage; bridge active for RPC."
          );
        }
      })
      .catch((err: unknown) => {
        mounted = false;
        const message = err instanceof Error ? err.message : String(err);
        replyToSource(ev, { type: "THREEKIT_CPQ_ERROR", message });
      });
  };

  window.addEventListener("message", onMessage);
  window.parent.postMessage({ type: "THREEKIT_CPQ_IFRAME_LOADED" }, "*");
}
